import { Cell } from '@/components/Bicester/Table/Cells/index';
import React from 'react';
import { TableCellProps } from 'rsuite';
import options from '@/utils/options';
import { RsPicker, RsInput } from '../..';
import { ItemDataType, TypeAttributes } from 'rsuite5/esm/@types/common';
import { getLabel } from '../../Card/Picker';
import { onChangeRowData } from '@/views/SubscribeService/utils';
import { InputNumber } from 'rsuite5';
import { FORM_DEFAULT_DISPLAY } from '@/constants/config';
import _ from 'lodash';

/**
 * picker属性
 */
interface PickerProps {
  /**
   * 下拉框数据
   */
  pickerData?: ItemDataType[];
  /**
   * 是否带搜索框
   */
  searchable?: boolean;
  /**
   * 下拉框最大高度
   */
  menuMaxHeight?: number;
  placement?: TypeAttributes.Placement;
}

/**
 * inputNumber属性
 */
interface InputNumberProps {
  /**
   * 空字符串时Blur失焦后自动填入
   */
  autoFill?: boolean;
  /**
   * 最大值
   */
  max?: number;
  /**
   * 最小值
   */
  min?: number;
  /**
   * 输入框前的label
   */
  before?: string;
  /**
   * 输入框后的label
   */
  after?: string;
}

/**
 * 可编辑的Cell
 */
const EditableCell = ({
  rowData,
  dataKey,
  labelKey,
  setState,
  editComponent = 'input',
  pickerData = [],
  searchable = false,
  menuMaxHeight,
  placement,
  autoFill = true,
  max = Infinity,
  min = -Infinity,
  before,
  after,
  ...props
}: {
  rowData?: any;
  dataKey?: string;
  /**
   * rowData[labelKey]是 ?? 右边的值
   */
  labelKey?: any;
  setState: (state) => void;
  editComponent?: 'input' | 'picker' | 'inputNumber';
} & TableCellProps &
  PickerProps &
  InputNumberProps) => {
  const editing = rowData?.isEdit ?? false;
  const data = _.get(rowData, dataKey, FORM_DEFAULT_DISPLAY);

  return (
    <Cell {...props} className={`editable-cell ${editing ? 'table-content-editing' : ''}`}>
      {editing
        ? options(
            {
              input: () => (
                <RsInput
                  defaultValue={rowData?.[dataKey]}
                  onChange={(value) => {
                    onChangeRowData?.(rowData?.id, { [dataKey]: value }, setState);
                  }}
                />
              ),
              picker: () => (
                <RsPicker
                  defaultValue={rowData?.[dataKey]}
                  data={pickerData}
                  searchable={searchable}
                  menuMaxHeight={menuMaxHeight}
                  onChange={(value) => {
                    onChangeRowData?.(rowData?.id, { [dataKey]: value }, setState);
                  }}
                  placement={placement}
                />
              ),
              inputNumber: () => (
                <>
                  {before && <span className="mr-[5px]">{before}</span>}
                  <InputNumber
                    className="custom-input-number"
                    value={data}
                    onChange={(val) => onChangeRowData?.(rowData?.id, { [dataKey]: val }, setState)}
                    onBlur={(event) => {
                      const value = Number(data);
                      if (_.isNaN(value) || data === '') {
                        autoFill && onChangeRowData(rowData?.id, { [dataKey]: min }, setState);
                      } else if (value >= min && value <= max) {
                        onChangeRowData(rowData?.id, { [dataKey]: Math.round(data) }, setState);
                      } else {
                        onChangeRowData(
                          rowData?.id,
                          { [dataKey]: value < min ? min : max },
                          setState
                        );
                      }
                    }}
                  />
                  {after && <span className="ml-[5px]">{after}</span>}
                </>
              ),
            },
            editComponent
          )
        : options(
            {
              input: () => (
                <span className="table-content-edit-span">
                  {rowData?.[dataKey] || rowData?.[labelKey] || ''}
                </span>
              ),
              picker: () => (
                <span className="table-content-edit-span">
                  {getLabel(rowData?.[dataKey], pickerData) || rowData?.[labelKey] || ''}
                </span>
              ),
              inputNumber: () => (
                <span className="table-content-edit-span">
                  {!_.isNil(rowData?.[dataKey]) ? `${before || ''}${data}${after || ''}` : ''}
                </span>
              ),
            },
            editComponent
          )}
    </Cell>
  );
};

export default EditableCell;
